import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Result, Select, Space, Table, Tabs, Tag, Typography, notification,
} from 'antd';
import {
  EditOutlined, LinkOutlined, PlayCircleOutlined, PlusOutlined, QuestionCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';

const { Title, Paragraph, Text } = Typography;
const PERMS = ['help_content.manage', 'admin.*'];

const KIND_LABEL = { embed: 'Plays in the portal', file: 'Video file — plays in the portal', link: 'Opens in a new tab' };

function VideoPlayer({ video, onClose }) {
  return (
    <Modal open={Boolean(video)} onCancel={onClose} footer={null} width={820} title={video?.title} destroyOnClose>
      {video?.kind === 'embed' ? (
        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            src={video.embed_url}
            title={video.title}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, borderRadius: 8 }}
          />
        </div>
      ) : video?.kind === 'file' ? (
        <video src={video.embed_url} controls autoPlay style={{ width: '100%', borderRadius: 8, background: '#000' }}>
          <track kind="captions" />
        </video>
      ) : video ? (
        <Paragraph>
          This link opens in a new tab in the portal: <a href={video.url} target="_blank" rel="noreferrer">{video.url}</a>
        </Paragraph>
      ) : null}
    </Modal>
  );
}

/** Add / edit one tutorial video link. */
function VideoDrawer({ open, video, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(video ? {
      title: video.title, description: video.description, sort_order: video.sort_order, url: video.url,
    } : { sort_order: 0 });
    setError(null);
  }, [open, video, form]);

  const save = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_) {
      return;
    }
    setSaving(true);
    setError(null);
    const res = video ? await lib.helpContent.updateVideo(video.id, values) : await lib.helpContent.createVideo(values);
    setSaving(false);
    if (res.error) {
      setError(res.message);
      return;
    }
    notification.success({ message: video ? 'Video updated — guides using it follow the change' : 'Video added' });
    onSaved();
  };

  return (
    <Drawer
      open={open}
      onClose={saving ? undefined : onClose}
      width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth : 560)}
      title={video ? 'Edit tutorial video' : 'Add a tutorial video'}
      destroyOnClose
      footer={(
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={save}>Save</Button>
        </Space>
      )}
    >
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: 'Add a title' }, { max: 160 }]}>
          <Input placeholder="e.g. Submitting Claims" />
        </Form.Item>
        <Form.Item
          name="url"
          label="Video link"
          extra="YouTube and Vimeo links, and direct .mp4 / .webm links, play inside the portal. Other links open in a new tab."
          rules={[
            { required: true, whitespace: true, message: 'Add the video link' },
            { type: 'url', message: 'Use a full link starting with https://' },
          ]}
        >
          <Input prefix={<LinkOutlined />} placeholder="https://www.youtube.com/watch?v=…" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} maxLength={2000} showCount placeholder="What this tutorial shows" />
        </Form.Item>
        <Form.Item
          name="sort_order"
          label="Position in Tutorials"
          extra="Lower numbers come first — order videos the way facilities usually do the tasks."
        >
          <InputNumber min={0} max={999} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

function GuideDrawer({ open, guide, pages, videos, defaultPage, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(guide ? {
      page_key: [guide.page_key], title: guide.title, body: guide.body, video_id: guide.video_id ?? undefined, sort_order: guide.sort_order,
    } : { page_key: defaultPage ? [defaultPage] : [], sort_order: 0 });
    setError(null);
  }, [open, guide, defaultPage, form]);

  const save = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_) {
      return;
    }
    const rawKey = Array.isArray(values.page_key) ? values.page_key[0] : values.page_key;
    const pageKey = String(rawKey || '').trim().toLowerCase().replace(/\s+/g, '_');
    const body = { ...values, page_key: pageKey, video_id: values.video_id ?? null };
    setSaving(true);
    const res = guide ? await lib.helpContent.updateGuide(guide.id, body) : await lib.helpContent.createGuide(body);
    setSaving(false);
    if (res.error) {
      setError(res.message);
      return;
    }
    notification.success({ message: guide ? 'Guide updated' : 'Guide added' });
    onSaved();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={Math.min(640, typeof window !== 'undefined' ? window.innerWidth : 640)}
      title={guide ? 'Edit guide' : 'New How To guide'}
      destroyOnClose
      footer={(
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={save}>Save</Button>
        </Space>
      )}
    >
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="page_key"
          label="Facility Portal page or section"
          extra="Pick a page, or type a new key (lower case, e.g. referrals) for a page added later."
          rules={[{ required: true, type: 'array', min: 1, message: 'Choose where this guide appears' }]}
        >
          <Select
            mode="tags"
            maxCount={1}
            showSearch
            optionFilterProp="label"
            placeholder="Choose a page"
            options={pages.map((p) => ({ value: p.key, label: `${p.label}${p.known ? '' : ` (${p.key})`}` }))}
          />
        </Form.Item>
        <Form.Item name="title" label="Guide title (shown on its tab)" rules={[{ required: true, whitespace: true, message: 'Add a title' }, { max: 160 }]}>
          <Input placeholder="e.g. Submit Claims" />
        </Form.Item>
        <Form.Item
          name="body"
          label="Instructions"
          extra="Plain text. Each line shows on its own line; start lines with 1. 2. 3. for steps."
          rules={[{ required: true, whitespace: true, message: 'Write the instructions' }]}
        >
          <Input.TextArea rows={10} maxLength={20000} showCount />
        </Form.Item>
        <Form.Item name="video_id" label="Tutorial video (optional)">
          <Select
            allowClear
            placeholder="No video"
            options={videos.map((v) => ({ value: v.id, label: v.title }))}
          />
        </Form.Item>
        <Form.Item name="sort_order" label="Tab position on the page" extra="Lower numbers come first.">
          <InputNumber min={0} max={999} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

/**
 * Facilities → Portal help & tutorials (Facility Portal Redesign §4.3, §4.5).
 * How To guides for any Facility Portal page, and the tutorial video library
 * they and the portal's Tutorials section share.
 */
export default function HelpContentPage() {
  const { hasAnyPermission } = usePermissions();
  const allowed = hasAnyPermission(PERMS);
  const [tab, setTab] = useState('guides');
  const [guides, setGuides] = useState([]);
  const [pages, setPages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageFilter, setPageFilter] = useState(null);
  const [guideDrawer, setGuideDrawer] = useState(null); // null | { guide? }
  const [videoDrawer, setVideoDrawer] = useState(null); // null | { video? }
  const [playing, setPlaying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [g, v] = await Promise.all([lib.helpContent.guides(), lib.helpContent.videos()]);
    if (g.error || v.error) notification.error({ message: 'Could not load help content', description: g.message || v.message });
    setGuides(g?.data?.guides || []);
    setPages(g?.data?.pages || []);
    setVideos(v?.data?.videos || []);
    setLoading(false);
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const pageLabel = useMemo(() => Object.fromEntries(pages.map((p) => [p.key, p.label])), [pages]);
  const countByPage = useMemo(() => {
    const m = {};
    guides.forEach((g) => { m[g.page_key] = (m[g.page_key] || 0) + 1; });
    return m;
  }, [guides]);
  const shownGuides = pageFilter ? guides.filter((g) => g.page_key === pageFilter) : guides;

  const removeGuide = async (g) => {
    const res = await lib.helpContent.deleteGuide(g.id);
    if (res.error) notification.error({ message: 'Could not delete', description: res.message });
    else { notification.success({ message: 'Guide deleted' }); load(); }
  };

  const removeVideo = async (v) => {
    const res = await lib.helpContent.deleteVideo(v.id);
    if (res.error) notification.error({ message: 'Could not delete', description: res.message });
    else {
      notification.success({
        message: 'Video deleted',
        description: res.data?.guides_unlinked ? `Removed from ${res.data.guides_unlinked} guide(s); their Watch Video option is gone.` : undefined,
      });
      load();
    }
  };

  if (!allowed) {
    return <Result status="403" title="Not available" subTitle="Managing Facility Portal help requires the help_content.manage permission." />;
  }

  return (
    <div style={{ padding: 16 }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%', marginBottom: 8 }} wrap>
        <Title level={3} style={{ margin: 0 }}><QuestionCircleOutlined /> Portal help &amp; tutorials</Title>
        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>Refresh</Button>
          {tab === 'guides' ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setGuideDrawer({})}>New guide</Button>
          ) : (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setVideoDrawer({})}>Add video</Button>
          )}
        </Space>
      </Space>
      <ModuleNav items={MODULE_NAVS.facilities} />
      <Paragraph type="secondary">
        Facility Portal pages with at least one guide show a <strong>How To</strong> button; several guides on a page appear as tabs.
        Tutorial videos appear in the portal&apos;s <strong>Tutorials</strong> section and as <strong>Watch video</strong> on the guides that use them.
      </Paragraph>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'guides',
            label: `How To guides · ${guides.length}`,
            children: (
              <>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Tag.CheckableTag checked={!pageFilter} onChange={() => setPageFilter(null)}>All pages</Tag.CheckableTag>
                  {pages.filter((p) => countByPage[p.key]).map((p) => (
                    <Tag.CheckableTag key={p.key} checked={pageFilter === p.key} onChange={() => setPageFilter(p.key)}>
                      {p.label} · {countByPage[p.key]}
                    </Tag.CheckableTag>
                  ))}
                </Space>
                <Table
                  size="small"
                  rowKey="id"
                  loading={loading}
                  dataSource={shownGuides}
                  pagination={{ pageSize: 25 }}
                  locale={{ emptyText: <Empty description="No guides yet — add the first one with New guide" /> }}
                  columns={[
                    { title: 'Page', key: 'page', width: 220, render: (_, g) => <Tag>{pageLabel[g.page_key] || g.page_key}</Tag> },
                    {
                      title: 'Guide',
                      key: 'title',
                      render: (_, g) => (
                        <div>
                          <Text strong>{g.title}</Text>
                          <div style={{ color: '#64748b', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 54, overflow: 'hidden' }}>{g.body}</div>
                        </div>
                      ),
                    },
                    {
                      title: 'Video',
                      key: 'video',
                      width: 220,
                      render: (_, g) => (g.video ? (
                        <Button size="small" type="link" icon={<PlayCircleOutlined />} onClick={() => setPlaying(g.video)}>{g.video.title}</Button>
                      ) : <Text type="secondary">—</Text>),
                    },
                    { title: 'Tab', dataIndex: 'sort_order', key: 'sort', width: 60 },
                    {
                      title: '',
                      key: 'actions',
                      width: 150,
                      render: (_, g) => (
                        <Space size={4}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => setGuideDrawer({ guide: g })}>Edit</Button>
                          <Popconfirm title="Delete this guide?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => removeGuide(g)}>
                            <Button size="small" danger>Delete</Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'videos',
            label: `Tutorial videos · ${videos.length}`,
            children: (
              <Table
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={videos}
                pagination={{ pageSize: 25 }}
                locale={{ emptyText: <Empty description="No videos yet — add one with Add video" /> }}
                columns={[
                  { title: '#', dataIndex: 'sort_order', key: 'sort', width: 50 },
                  {
                    title: 'Video',
                    key: 'video',
                    render: (_, v) => (
                      <div>
                        <Text strong>{v.title}</Text>
                        {v.description ? <div style={{ color: '#64748b', fontSize: 12 }}>{v.description}</div> : null}
                      </div>
                    ),
                  },
                  {
                    title: 'Link',
                    key: 'link',
                    width: 260,
                    render: (_, v) => (
                      <div style={{ fontSize: 12 }}>
                        <a href={v.url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}><LinkOutlined /> {v.url}</a>
                        <div><Tag color={v.kind === 'link' ? 'default' : 'blue'} style={{ marginTop: 4 }}>{KIND_LABEL[v.kind]}</Tag></div>
                      </div>
                    ),
                  },
                  { title: 'Used by', key: 'used', width: 90, render: (_, v) => (v.guide_count ? `${v.guide_count} guide${v.guide_count === 1 ? '' : 's'}` : '—') },
                  {
                    title: '',
                    key: 'actions',
                    width: 250,
                    render: (_, v) => (
                      <Space size={4} wrap>
                        <Button size="small" icon={<PlayCircleOutlined />} onClick={() => setPlaying(v)}>Play</Button>
                        <Button size="small" icon={<EditOutlined />} onClick={() => setVideoDrawer({ video: v })}>Edit</Button>
                        <Popconfirm
                          title="Delete this video?"
                          description={v.guide_count
                            ? `${v.guide_count} guide(s) use it; they keep their text but lose Watch Video.`
                            : 'It is removed from Tutorials.'}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => removeVideo(v)}
                        >
                          <Button size="small" danger>Delete</Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            ),
          },
        ]}
      />

      <GuideDrawer
        open={Boolean(guideDrawer)}
        guide={guideDrawer?.guide}
        pages={pages}
        videos={videos}
        defaultPage={pageFilter || undefined}
        onClose={() => setGuideDrawer(null)}
        onSaved={() => { setGuideDrawer(null); load(); }}
      />
      <VideoDrawer
        open={Boolean(videoDrawer)}
        video={videoDrawer?.video}
        onClose={() => setVideoDrawer(null)}
        onSaved={() => { setVideoDrawer(null); load(); }}
      />
      <VideoPlayer video={playing} onClose={() => setPlaying(null)} />
    </div>
  );
}
